import { Form, Formik } from 'formik';
import { filter, find, isEmpty, sortBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseDialogHandler,
  CloseDialogResult,
  Dialog,
  FormControls,
  FormikSelect,
} from 'components';
import { useP2PProvidersTradeMethods, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { formUtils } from 'utils';

type Values = {
  providerId: string;
};

type Props = {
  open: boolean;
  selectedCount: number;
  onClose: CloseDialogHandler<Values>;
};

const INITIAL_VALUES: Values = { providerId: '' };

export const AssignProviderDialog: React.FC<Props> = ({
  open,
  selectedCount,
  onClose,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.manual.assign_dialog',
  });
  const { p2pProviders } = useUserContext();
  const { tradeMethodsByProviderId } = useP2PProvidersTradeMethods(open);

  // Providers with no payout trade methods at all can never serve a payout — keep them out of the
  // list. Everything finer (balance, limits, the specific method) is validated by the backend.
  const filteredProviders = useMemo(
    () =>
      sortBy(
        filter(
          p2pProviders,
          (provider) =>
            !provider.archived &&
            !isEmpty(
              tradeMethodsByProviderId[provider.id]?.payoutConfig
                ?.tradeMethodIds,
            ),
        ),
        'name',
      ),
    [p2pProviders, tradeMethodsByProviderId],
  );

  const options = useMemo(
    () => formUtils.getOptions(filteredProviders),
    [filteredProviders],
  );

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        providerId: Yup.string().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const handleClose = useCallback(
    (result: CloseDialogResult<Values>) => onClose(result),
    [onClose],
  );

  const handleSubmit = useCallback(
    (values: Values) => handleClose({ ok: true, data: values }),
    [handleClose],
  );

  const getProviderName = useCallback(
    (providerId: string) => find(filteredProviders, { id: providerId })?.name,
    [filteredProviders],
  );

  return (
    // Remount on every open: a provider left over from the previous batch must never be
    // pre-selected for the next one.
    <Formik
      key={String(open)}
      initialValues={INITIAL_VALUES}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Dialog
          open={open}
          title={t('title')}
          okDisabled={!formik.isValid}
          data={formik.values}
          onClose={handleClose}
        >
          <Form>
            <div className="tw-mb-4">
              {t('description', { count: selectedCount })}
            </div>
            <FormControls>
              <FormikSelect
                name="providerId"
                label={t('provider')}
                options={options}
                disablePortal={false}
              />
            </FormControls>
            {!!formik.values.providerId && (
              <div className="tw-mt-4">
                {t('confirmation', {
                  count: selectedCount,
                  name: getProviderName(formik.values.providerId),
                })}
              </div>
            )}
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};
